import { InformationCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { Loading } from '../../utils/Loading';
import { useHistory } from '../../providers/HistoryProvider';
import { FileUploader } from '../recordsrecap/FileUploader';
import TemplatePicker from '../templates/TemplatePicker';
import { TemplateType } from '@neum/frontend-shared/constants/templateData';
import { RecapView } from '../recordsrecap/RecapView';
import RecapErrorBanner from '../recordsrecap/RecapErrorBanner';
import { PiMS } from '../../utils/PimsUtils';
import { VetspireRecapOptions } from '../integrations/IntegrationUtils';
import { v4 as uuidv4 } from 'uuid';

export default function PreHistory() {
    const {
        activeSession,
        preHistoryLoading,
        preHistoryStreaming,
        preHistoryProcessingMessage,
        activePreHistory,
        shouldShowPrehistoryScannedDocumentsMessage,
        recapSubmitting,
        templateRecap,
        preHistoryFailureMessage,
        setTemplateRecap,
        handleSubmitRecap,
        setError,
    } = useHistory();

    async function onSubmitButtonClick(files: File[], externalPatientId?:string, source?:PiMS, advancedOptions?:VetspireRecapOptions) {
        if(!activeSession){
            setError("There was an issue processing the pre-history. Please try again.")
            return
        }

        await handleSubmitRecap({
            files,
            withFiles: files.length > 0,
            isRegen: false,
            uploadId: uuidv4(),
            externalPatientId,
            source,
            advancedOptions,
        })
    }

    return (<div className='mb-8 flex flex-col gap-y-4'>
        {preHistoryLoading && !activePreHistory && <Loading text={preHistoryLoading}/>}
        <RecapErrorBanner />
        {!preHistoryFailureMessage && !preHistoryLoading && !activePreHistory && <>
            {!recapSubmitting && !preHistoryProcessingMessage && !preHistoryStreaming && <div className='flex flex-col gap-y-4'>
                <div className="flex sm:flex-row flex-col w-full gap-x-4 gap-y-4 pt-2 items-center">
                    <div className="rounded-md bg-blue-100 shadow grow flex items-center px-3 py-4 w-full gap-x-2 font-semibold">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                        <p className="text-sm text-blue-900">
                            Summarize prior records, including scanned and handwritten notes! Customize recaps with <a target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700" href="templates">custom templates</a>.
                        </p>
                    </div>
                    <TemplatePicker 
                        template={templateRecap} 
                        templateIdForce={activeSession?.prehistory_metadata?.template_id}
                        setTemplate={setTemplateRecap} 
                        className='h-10 sm:h-14' 
                        templateBackground='bg-white' 
                        type={TemplateType.RECORDSRECAP}/>
                </div>
                <FileUploader sessionId={activeSession?.id} onSubmitButtonClick={onSubmitButtonClick} externalPatientIdForce={activeSession?.external_patient_id}/>
            </div>}
            {(recapSubmitting || preHistoryProcessingMessage || preHistoryStreaming) && <div className='mt-8 mb-8'>
                <Loading text={preHistoryProcessingMessage ?? "Processing prior records"}/>
                {preHistoryProcessingMessage && <div className='flex flex-col justify-center items-center -mt-4 text-sm'>Feel free to navigate away if you need to</div>}
            </div>}
            {shouldShowPrehistoryScannedDocumentsMessage && <div className='mt-8 mb-8'>
                <div className='flex flex-col justify-center items-center text-sm bg-gradient-to-r from-[#cce0ff] to-[#d9fff6] rounded-lg p-4 border border-blue-200 max-w-2xl mx-auto'>
                    <div className='flex items-center gap-x-2 text-blue-900'>
                        <SparklesIcon className="h-5 w-5" />
                        <span className='font-semibold'>Records Recap Update</span>
                    </div>
                    <div className='text-blue-900 mt-1'>
                        We have made some changes to how we process your records. This might take a bit longer than normal as the system is being optimized to improve the quality of the summaries.
                    </div>
                </div>
            </div>}
        </>}
        {activeSession && activePreHistory && <RecapView />}
    </div>)
}
